import React from 'react';
import './Measurements.css';

interface Measurement {
    id: string;
    timestamp: string;
    weight?: number;
    bodyFatPercentage?: number;
    muscleMass?: number;
    [key: string]: any;
}

interface DetailedMeasurementsTableProps {
    measurements: Measurement[];
}

export const DetailedMeasurementsTable: React.FC<DetailedMeasurementsTableProps> = ({ measurements }) => {
    const formatDate = (iso: string) => {
        const d = new Date(iso);
        return d.toLocaleDateString('pt-BR');
    };

    const calculateDelta = (current: number | undefined, previous: number | undefined) => {
        if (current === undefined || previous === undefined) return null;
        const diff = current - previous;
        return diff > 0 ? `+${diff.toFixed(1)}` : diff.toFixed(1);
    };

    return (
        <div className="ns-table-wrapper">
            <table className="ns-table">
                <thead>
                    <tr>
                        <th>Data</th>
                        <th>Peso (kg)</th>
                        <th>Gordura (%)</th>
                        <th>Massa Muscular (kg)</th>
                        <th>Dobras (mm)</th>
                    </tr>
                </thead>
                <tbody>
                    {measurements.map((m, i) => {
                        const prev = measurements[i + 1];
                        const weightDelta = calculateDelta(m.weight, prev?.weight);
                        const fatDelta = calculateDelta(m.bodyFatPercentage, prev?.bodyFatPercentage);
                        const muscleDelta = calculateDelta(m.muscleMass, prev?.muscleMass);

                        // Sum of skinfolds if available
                        const skinfolds = [
                            m.tricepsSkinfold, m.bicepsSkinfold, m.subscapularSkinfold,
                            m.suprailiacSkinfold, m.abdominalSkinfold, m.thighSkinfold, m.calfSkinfold
                        ].filter(v => v !== undefined && v !== null) as number[];
                        const skinfoldSum = skinfolds.length > 0 ? skinfolds.reduce((a, b) => a + b, 0) : null;

                        const prevSkinfolds = prev ? [
                            prev.tricepsSkinfold, prev.bicepsSkinfold, prev.subscapularSkinfold,
                            prev.suprailiacSkinfold, prev.abdominalSkinfold, prev.thighSkinfold, prev.calfSkinfold
                        ].filter(v => v !== undefined && v !== null) as number[] : [];
                        const prevSkinfoldSum = prevSkinfolds.length > 0 ? prevSkinfolds.reduce((a, b) => a + b, 0) : null;

                        const skinfoldDelta = calculateDelta(skinfoldSum || undefined, prevSkinfoldSum || undefined);

                        return (
                            <tr key={m.id}>
                                <td>{formatDate(m.timestamp)}</td>
                                <td>
                                    {m.weight ? m.weight.toFixed(1) : '-'}
                                    {weightDelta && <span className={`ns-delta ${parseFloat(weightDelta) > 0 ? 'positive' : 'negative'}`}>{weightDelta}</span>}
                                </td>
                                <td>
                                    {m.bodyFatPercentage ? m.bodyFatPercentage.toFixed(1) : '-'}
                                    {fatDelta && <span className={`ns-delta ${parseFloat(fatDelta) < 0 ? 'positive' : 'negative'}`}>{fatDelta}</span>}
                                </td>
                                <td>
                                    {m.muscleMass ? m.muscleMass.toFixed(1) : '-'}
                                    {muscleDelta && <span className={`ns-delta ${parseFloat(muscleDelta) > 0 ? 'positive' : 'negative'}`}>{muscleDelta}</span>}
                                </td>
                                <td>
                                    {skinfoldSum ? skinfoldSum.toFixed(1) : '-'}
                                    {skinfoldDelta && <span className={`ns-delta ${parseFloat(skinfoldDelta) < 0 ? 'positive' : 'negative'}`}>{skinfoldDelta}</span>}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};
